import React from "react"
import styled from "styled-components"
import { inject, observer } from "mobx-react"
import Link from "next/link"
import { withRouter } from "next/router"
import dynamic from "next/dynamic"
import { useTranslations } from "next-intl"

import DesktopContainer from "~/components/DesktopContainer"
import WinItButton from "~/components/WinItButton"
import mobileMenuLogo from "~/public/static/images/svg/winItMobileMenuNoShadow.svg"
import closeMenuLogo from "~/public/static/images/svg/closeMenuLogo.svg"
import HeaderSupportPhoneNumber from "~/components/altHeaders/HeaderSupportPhoneNumber"
import PHONE_NUMBERS from "~/components/altHeaders/phoneNumbers"
import withTranslations from "./_hocs/withTranslations"

import defaultLinks from "./links"

const LoginModal = dynamic(() => import("~/components/auth/LoginModal"))
const SignupModal = dynamic(() => import("~/components/auth/SignUpModal"))

const Main = styled.div`
  position: relative;
  width: 100%;
  overflow: ${(props) => (props.open ? "visible" : "hidden")};
`

const BarContainer = styled.div`
  display: flex;
  width: 100%;
  height: ${(props) => (props.height ? props.height : "56px")};
  align-items: center;
  justify-content: space-between;
`

const LogoContainer = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: flex-start;
  align-self: flex-start;
`

const MobileLogo = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const LinkContainer = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  height: 100%;
  align-items: center;
  justify-content: space-around;
  justify-self: flex-end;
`

const MobileLinks = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: center;
  justify-content: center;
`

const ButtonContainer = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  margin-left: 1.5625rem;
  align-items: center;
  justify-content: center;
  justify-self: flex-end;
`

const LinkWrapper = styled.div`
  ${(props) => (props.lastItem ? null : `margin-right: 1.5625rem;`)};
  ${(props) => props.theme.media.mobile`
    margin-right: 0;
    margin-top: 2.3em;
  `}
`

const LinkText = styled.a`
  font-size: 0.875rem;
  font-family: ${(props) => props.theme.fonts.avenirNextRegular};
  color: ${(props) => props.theme.colors.text.label};
  cursor: pointer;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  ${(props) => props.theme.media.mobile`
    font-size: 1.5625rem;
  `}
`

const Menu = styled.div`
  position: fixed;
  width: 100%;
  height: 100vh;
  padding: 10px;
  padding-top: 25px;
  right: 0;
  overflow: noscroll;
  background: #fff;
  list-style-type: none;
  -webkit-font-smoothing: antialiased;
  /* to stop flickering of text in safari */

  transform-origin: 0% 0%;
  transform: ${(props) => (props.open ? "none" : "translate(100%, 0);")};
  transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1);
  z-index: 30;
  ${(props) => props.theme.media.mobile`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `}
`

const ShowContainer = styled.div`
  display: ${(props) => (props.showOnCollapse ? "none" : "flex")};
  ${(props) => props.theme.media.mobile`
    display: ${props.showOnCollapse ? "flex" : "none"};
  `}
`

const MenuButton = styled.img`
  position: relative;
`

const CloseButton = styled.img`
  position: absolute;
  right: 1rem;
  top: 2rem;
`

const Login = ({ onClick }) => {
  const translate = useTranslations("header")

  return <LinkText onClick={onClick}>{translate("login")}</LinkText>
}

const WinItSubmit = ({ onClick }) => {
  const translate = useTranslations("header")

  return (
    <WinItButton
      color="blue"
      height={32}
      width="106px"
      shape="round"
      fontSize="14px"
      onClick={onClick}
    >
      {translate("signUp")}
    </WinItButton>
  )
}

@inject((all) => ({
  isLoggedIn: all.store.auth.isLoggedIn,
  auth: all.store.auth,
  isMobile: all.store.ui.isMobile,
  fetcherName: all.store.trafficLookup.fetcherName,
  isStateAffiliatedWithWinitLaw:
    all.store.trafficLookup.isStateAffiliatedWithWinitLaw,
}))
@observer
class Header extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      open: false,
    }
  }

  componentDidMount() {
    window.addEventListener("resize", this.updateMenu)
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateMenu)
  }

  updateMenu = () => {
    if (window.innerWidth > 860 && this.state.open) {
      this.setState({
        open: false,
      })
    }
  }

  toggle = () => this.setState({ open: !this.state.open })

  logout = () => {
    this.props.auth.logout()
    if (this.state.open) this.toggle()
  }

  render() {
    const links = this.props.links ? this.props.links : defaultLinks
    const isTrafficLookup = this.props.router.pathname.includes(
      "/traffic/lookup"
    )

    let phoneNumber
    if (this.props.isMobile && isTrafficLookup) {
      phoneNumber =
        PHONE_NUMBERS.TRAFFIC_LOOKUP[this.props.fetcherName] ||
        PHONE_NUMBERS.TRAFFIC_LOOKUP.NY
    } else phoneNumber = PHONE_NUMBERS.DEFAULT
    const { isStateAffiliatedWithWinitLaw } = this.props
    return (
      <>
        <Main open={this.state.open}>
          <Menu open={this.state.open}>
            <MobileLogo>
              {this.props.mobileLogo ? this.props.mobileLogo : ""}
            </MobileLogo>
            <CloseButton src={closeMenuLogo} onClick={this.toggle} />
            <MobileLinks>
              {links.map(({ tag, url, otherApp }) => {
                if (otherApp)
                  return (
                    <LinkWrapper key={tag}>
                      <LinkText href={url}>{tag}</LinkText>
                    </LinkWrapper>
                  )
                return (
                  <LinkWrapper key={tag}>
                    <Link href={url} passHref>
                      <LinkText>{tag}</LinkText>
                    </Link>
                  </LinkWrapper>
                )
              })}
              {this.props.isLoggedIn ? (
                <LinkWrapper lastItem>
                  <LinkText onClick={this.logout}>
                    {this.props.translate("signOut")}
                  </LinkText>
                </LinkWrapper>
              ) : (
                <LinkWrapper>
                  <LoginModal opener={Login} />
                </LinkWrapper>
              )}
              {this.props.showMobileSignUp && !this.props.isLoggedIn ? (
                <LinkWrapper lastItem>
                  <SignupModal opener={WinItSubmit} />
                </LinkWrapper>
              ) : null}
              {this.props.mobileMenuBottomLogo
                ? this.props.mobileMenuBottomLogo
                : null}
            </MobileLinks>
          </Menu>
          <DesktopContainer>
            <BarContainer height={this.props.height}>
              <LogoContainer>
                {this.props.headerLogo ? this.props.headerLogo : ""}
              </LogoContainer>
              {!isStateAffiliatedWithWinitLaw && (
                <MenuContainer>
                  <ShowContainer>
                    <LinkContainer>
                      {links.map(({ tag, url, otherApp }) => {
                        if (otherApp) {
                          return (
                            <LinkWrapper key={tag}>
                              <LinkText href={url}>{tag}</LinkText>
                            </LinkWrapper>
                          )
                        }
                        return (
                          <LinkWrapper key={tag}>
                            <Link href={url} passHref>
                              <LinkText>{tag}</LinkText>
                            </Link>
                          </LinkWrapper>
                        )
                      })}
                      {this.props.isLoggedIn && (
                        <LinkWrapper lastItem>
                          <LinkText onClick={this.logout}>
                            {this.props.translate("signOut")}
                          </LinkText>
                        </LinkWrapper>
                      )}
                      <LoginModal opener={Login} />
                    </LinkContainer>
                  </ShowContainer>
                  <ShowContainer showOnCollapse>
                    <HeaderSupportPhoneNumber phoneNumber={phoneNumber} />
                    {!this.props.noHamburger && (
                      <ButtonContainer>
                        <MenuButton
                          src={mobileMenuLogo}
                          onClick={this.toggle}
                        />
                      </ButtonContainer>
                    )}
                  </ShowContainer>
                </MenuContainer>
              )}
            </BarContainer>
          </DesktopContainer>
        </Main>
      </>
    )
  }
}

export default withRouter(withTranslations(Header, "header"))
